import type { Ageing, ReceivableItem } from '../api/outstanding';
import type { StatusTone } from '../vocabulary';

/**
 * The words and tones of the outstanding lists (step 6 of
 * `docs/Purchase_Sale_Outstanding_Design.md`). The amounts are the server's; this only names them.
 */

export interface AgeingBand {
  key: keyof Ageing;
  label: string;
  amount: number;
}

/**
 * The bands to show, in order. Customers have no "not yet due" band — a balance is owed from the
 * hand-over, and one on a booking not yet out is not on the list at all.
 */
export function ageingBands(ageing: Ageing, side: 'receive' | 'pay'): AgeingBand[] {
  const bands: AgeingBand[] = [
    { key: 'upTo30', label: '0–30 days', amount: ageing.upTo30 },
    { key: 'upTo60', label: '31–60 days', amount: ageing.upTo60 },
    { key: 'upTo90', label: '61–90 days', amount: ageing.upTo90 },
    { key: 'over90', label: 'Over 90 days', amount: ageing.over90 },
  ];

  return side === 'pay'
    ? [{ key: 'notYetDue', label: 'Not yet due', amount: ageing.notYetDue }, ...bands]
    : bands;
}

/** How long a customer has owed: "since today", "1 day", "45 days". */
export function owedForWords(days: number): string {
  if (days <= 0) {
    return 'since today';
  }

  return days === 1 ? '1 day' : `${days} days`;
}

/** Where a vendor's due date stands: "due today", "due in 3 days", "12 days late". */
export function dueWords(daysPastDue: number): string {
  if (daysPastDue === 0) {
    return 'due today';
  }

  const days = Math.abs(daysPastDue);
  const count = days === 1 ? '1 day' : `${days} days`;

  return daysPastDue < 0 ? `due in ${count}` : `${count} late`;
}

/**
 * The tone of an age: a customer past 30 days, or a vendor past due, is a warning; past 60 (a
 * vendor past 30), or an MSME supplier past due at all — the 45-day line is already in the due
 * date — is negative.
 */
export function ageTone(days: number, side: 'receive' | 'pay', isMsme = false): StatusTone {
  if (side === 'pay') {
    if (days <= 0) {
      return 'neutral';
    }

    return isMsme || days > 30 ? 'negative' : 'warning';
  }

  if (days > 60) {
    return 'negative';
  }

  return days > 30 ? 'warning' : 'neutral';
}

/**
 * What a customer owes on, counted by kind: "3 bookings", "1 sale", "2 bookings · 1 sale". A
 * customer's list holds both since retail sales (purchase step 5); calling a sale a booking would
 * send the clerk looking for a booking that does not exist.
 */
export function owedOnWords(items: readonly Pick<ReceivableItem, 'kind'>[]): string {
  const count = (kind: ReceivableItem['kind'], one: string, many: string) => {
    const n = items.filter((item) => item.kind === kind).length;

    return n === 0 ? null : `${n} ${n === 1 ? one : many}`;
  };

  return [count('Booking', 'booking', 'bookings'), count('Sale', 'sale', 'sales')]
    .filter((part) => part !== null)
    .join(' · ');
}
